import React, { useImperativeHandle, forwardRef } from "react";
import PrintConfigration, { PrintData } from "src/print-configuration/PrintConfigration";
import { splitDateTime } from "src/shared/components/dateTime";

interface ExtendedCarOrderPrintProps {
  order: any;
  latestPayment?: {
    receivedAmount: number;
    paymentMethod: string;
    newReceivedAmount: number;
  };
  onPrintComplete?: () => void;
  triggerPrint?: boolean;
}

export interface CarOrderPrintRef {
  triggerPrint: () => void;
}

const CarOrderPrint = forwardRef<CarOrderPrintRef, ExtendedCarOrderPrintProps>(
  ({ order, latestPayment, onPrintComplete, triggerPrint }, ref) => {
    const printButtonRef = React.useRef<HTMLButtonElement | null>(null);

    useImperativeHandle(ref, () => ({
      triggerPrint: () => {
        printButtonRef.current?.click();
      },
    }));

    React.useEffect(() => {
      if (triggerPrint && printButtonRef.current) {
        setTimeout(() => {
          printButtonRef.current?.click();
        }, 100);
      }
    }, [triggerPrint]);


    const firstOrder = order?.orders?.[0];

    const latestOrder = order?.orders?.length
      ? order.orders[order.orders.length - 1]
      : undefined;

    const totalAmount = Number(firstOrder?.finalAmount || firstOrder?.orderAmount || 0);

    const paidAmount =
      order?.paymentHistory?.reduce(
        (sum: number, p: any) => sum + Number(p.receivedAmount || 0),
        0
      ) || 0;

    const remainingAmount = Math.max(0, totalAmount - paidAmount);


    const customerName = `${order?.customerDetails?.firstName || ""} ${
      order?.customerDetails?.lastName || ""
    }`.trim() || "N/A";

    const phoneNumber = order?.customerDetails?.phones?.[0]?.phone || "";

    const { date: orderDate, time: orderTime } = splitDateTime(
      firstOrder?.orderedOn?.$date ?? new Date().toISOString()
    );

    const paymentMethod =
      latestPayment?.paymentMethod ||
      order?.paymentHistory?.[order.paymentHistory.length - 1]?.paymentMethod ||
      "N/A";

    const products =
      order?.orderDetails?.[0]?.products?.map((p: any) => ({
        productName: p.name || "",
        arabicword: p.arabicName || "",
        amount: Number(p.unitPrice || p.lineTotal || 0),
        quantity: Number(p.quantity || 1),
        totalPrice: Number(p.lineTotal || p.unitPrice || 0),
        uom: p.uom || "",
      })) || [];

    const payMentHistory =
      order?.paymentHistory?.map((p: any) => ({
        Paymentmethod: p.paymentMethod,
        ReceivedAmount: p.receivedAmount,
        date: p.paidOn?.$date
          ? new Date(p.paidOn.$date).toLocaleDateString()
          : "",
        payment_on: p.paidOn?.$date
          ? new Date(p.paidOn.$date).toLocaleDateString()
          : "",
        balance: 0,
        paidAmount: p.receivedAmount,
        balanceReturned: p.balanceReturned || 0,
      })) || [];

    const printData: PrintData = {
      CustomerName: customerName,
      CustomerPhon: phoneNumber,
      OrderId: order?.orderId || "N/A",

      OrderStatus: latestOrder?.status || "Confirm",

      discount: Number(firstOrder?.discount || 0),

      Date: orderDate,
      Time: orderTime,
      businessType: "Carwash",

      Products: products,

      Paid: paidAmount,
      unpaid: remainingAmount,
      OSB: remainingAmount,

      paymentMethod: paymentMethod,

      subtotal: totalAmount,
      total: totalAmount,

      billNo: latestOrder?.billNumber,

      deliveryFee: Number(firstOrder?.deliveryFee || 0),

      payMentHistory: payMentHistory,
      payMentHistoryAvabile: payMentHistory.length > 0,

      clientId: order?.customerDetails?.clientId || order?.clientId,
      prvBalance: order?.previousOutstandingBalance || 0,
      prvPaid: order?.previousPaidAmount || 0,
    };

    const handleCloseAfterPrint = (closed: boolean) => {
      if (!closed && onPrintComplete) {
        onPrintComplete();
      }
    };

    const PrintWrapper = () => {
      const buttonRef = (node: any) => {
        if (node?.querySelector) {
          const btn = node.querySelector("button");
          if (btn) printButtonRef.current = btn;
        }
      };

      return (
        <div ref={buttonRef}>
          <PrintConfigration
            BtnName="Print"
            PrintData={printData}
            isClosed={handleCloseAfterPrint}
          />
        </div>
      );
    };

    return <PrintWrapper />;
  }
);

CarOrderPrint.displayName = "CarOrderPrint";

export default CarOrderPrint;